import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  deepObjectsCompare,
} from "@unified-checkout/design-system";
import { Header } from "./Header";
import { BusinessInfo } from "./BusinessInfo";
import { BusinessProfileSkeleton } from "./BusinessProfileSkeleton";
import { SocialMedia } from "./SocialMedia";
import { getBusinessInformation, updateBusinessInformation } from "@app/dashboard/services/inner_layout/business-information";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { notify } from "@app/dashboard/utils/notifier";
import {
  buildBusinessProfileFormData,
  mapBusinessInfoApiDataToFormState,
  mapBusinessInfoApiDataToOriginalValues,
  mapOriginalValuesToFormState,
  validateBusinessProfileForm,
} from "./utils";
import {
  EMPTY_FORM_ERRORS,
  EMPTY_FORM_STATE,
  EMPTY_ORIGINAL_VALUES,
} from "@app/dashboard/constants/business-profile";
import type { AxiosError } from "axios";
import type { FormState, OriginalValues, FormErrors } from "@app/dashboard/types/innerLayout/business_profile";
import styles from "./business-profile.module.scss";

export const BusinessProfile = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState<FormState>(EMPTY_FORM_STATE);
  const [formErrors, setFormErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [logoWasDeleted, setLogoWasDeleted] = useState(false);

  const hasAppliedInitialRetrieveRef = useRef(false);
  const originalValuesRef = useRef<OriginalValues | null>(null);
  const logoWasDeletedRef = useRef(false);

  const {
    isFetching: isBusinessInfoFetching,
    isLoading: isBusinessInfoLoading,
    isError: isBusinessInfoError,
    error: businessInfoError,
    data: businessInfoResponse,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BUSINESS_INFORMATION],
    queryFn: () => getBusinessInformation(),
    staleTime: 0,
    gcTime: 0,
  });

  const updateMutation = useMutation({
    mutationFn: (formData: FormData) => updateBusinessInformation(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_BUSINESS_INFORMATION] });
      notify.success(t('innerLayout.businessProfilePage.toasters.update.successText'), { timeout: 2 });
    },
    onError: (error: any) => {
      const axiosErr = error as AxiosError;
      const response = axiosErr?.response as any;

      const errorMessage = response?.data?.message ||
        t('innerLayout.businessProfilePage.toasters.update.failureText');
      notify.error(errorMessage);
    },
  });

  // Initialize form state and original values with business information from API response
  useEffect(() => {
    const isBusinessInfoRetrieved = businessInfoResponse?.data && !isBusinessInfoFetching && !hasAppliedInitialRetrieveRef.current;
    const isBusinessInfoApiError = isBusinessInfoError && !isBusinessInfoFetching && !hasAppliedInitialRetrieveRef.current;
    const isBusinessInfoUpdateSuccess = businessInfoResponse?.data && hasAppliedInitialRetrieveRef.current && updateMutation.isSuccess;

    if (isBusinessInfoRetrieved) {
      const mappedFormStateFromApi = mapBusinessInfoApiDataToFormState(businessInfoResponse?.data);

      setFormState(mappedFormStateFromApi);
      originalValuesRef.current = mapBusinessInfoApiDataToOriginalValues(businessInfoResponse?.data);
      hasAppliedInitialRetrieveRef.current = true;
      setIsFormInitialized(true);
      validateBusinessProfileForm(mappedFormStateFromApi, setFormErrors, t);
    } else if (isBusinessInfoApiError) {
      originalValuesRef.current = { ...EMPTY_ORIGINAL_VALUES };
      hasAppliedInitialRetrieveRef.current = true;
      setIsFormInitialized(true);

      const errorMessage =
        (businessInfoError as AxiosError<{ message?: string }>)?.response?.data?.message ||
        t('errors.somethingWentWrong');
      notify.error(errorMessage);
    } else if (isBusinessInfoUpdateSuccess) {
      const mappedFormStateFromApi = mapBusinessInfoApiDataToFormState(businessInfoResponse?.data);

      originalValuesRef.current = mapBusinessInfoApiDataToOriginalValues(businessInfoResponse?.data);
      setFormState((prev) => ({
        ...prev,
        whatsappPhone: mappedFormStateFromApi.whatsappPhone,
        whatsappCountry: mappedFormStateFromApi.whatsappCountry,
        logoImage: null,
      }));
      logoWasDeletedRef.current = false;
      setLogoWasDeleted(false);
    }
  }, [businessInfoResponse?.data, isBusinessInfoError, isBusinessInfoFetching, businessInfoError, updateMutation.isSuccess, t]);

  // Validate form state on initial load or language change
  useEffect(() => {
    if (!isFormInitialized) return;
    validateBusinessProfileForm(formState, setFormErrors, t);
  }, [i18n.language, isFormInitialized]);

  // Reset refs on unmount
  useEffect(() => {
    return () => {
      originalValuesRef.current = null;
      hasAppliedInitialRetrieveRef.current = false;
      logoWasDeletedRef.current = false;
    };
  }, []);

  const hasFormChanges = useMemo(() => {
    if (!originalValuesRef.current || !hasAppliedInitialRetrieveRef.current || !isFormInitialized) {
      return false;
    }

    const originalValues = originalValuesRef.current;
    const logoChanged = formState.logoImage instanceof File ||
      (logoWasDeletedRef.current && originalValues.originalLogoUrl != null);
    const formUnchanged = deepObjectsCompare(formState, originalValues, {
      ignoreKeys: ['logoImage', 'originalLogoUrl'],
    });

    return !formUnchanged || logoChanged;
  }, [formState, isFormInitialized]);

  const hasValidationErrors = useMemo(
    () => Object.values(formErrors).some((error) => error !== ""),
    [formErrors]
  );

  // Sync original values from form state to original values ref
  const syncOriginalValuesFromFormState = (savedFormState: FormState) => {
    originalValuesRef.current = {
      ...savedFormState,
      logoImage: null,
      originalLogoUrl: logoWasDeletedRef.current
        ? null
        : (originalValuesRef.current?.originalLogoUrl ?? null),
    };

    logoWasDeletedRef.current = false;
    setLogoWasDeleted(false);
    setFormState((prev) => ({ ...prev, logoImage: null }));
  };

  const handleLogoWasDeletedChange = (wasDeleted: boolean) => {
    logoWasDeletedRef.current = wasDeleted;
    setLogoWasDeleted(wasDeleted);
  };

  const handleCancel = () => {
    const originalValues = originalValuesRef.current;
    if (!originalValues) return;

    setFormState(mapOriginalValuesToFormState(originalValues));
    logoWasDeletedRef.current = false;
    setLogoWasDeleted(false);
    setFormErrors({ ...EMPTY_FORM_ERRORS });
  };

  const handleSave = () => {
    const originalValues = originalValuesRef.current;
    if (!hasFormChanges || updateMutation.isPending || !originalValues) return;

    const savedFormState = formState;

    updateMutation.mutate(
      buildBusinessProfileFormData(savedFormState, originalValues, logoWasDeletedRef.current),
      {
        onSuccess: () => {
          if (isBusinessInfoError) {
            syncOriginalValuesFromFormState(savedFormState);
          }
        },
      }
    );
  };

  const isBusinessInfoApiDataLoading = isBusinessInfoFetching || isBusinessInfoLoading;
  const isCancelDisabled = isBusinessInfoApiDataLoading || !isFormInitialized || !hasFormChanges || updateMutation.isPending;
  const isSaveDisabled = isBusinessInfoApiDataLoading || !isFormInitialized || !hasFormChanges || updateMutation.isPending || hasValidationErrors;

  if (isBusinessInfoApiDataLoading) {
    return <BusinessProfileSkeleton />;
  }

  return (
    <div className={styles["business-profile"]}>
      <Header />

      <div className={styles["business-profile__content"]}>
        <BusinessInfo
          formState={formState}
          formErrors={formErrors}
          setFormState={setFormState}
          setFormErrors={setFormErrors}
          onLogoWasDeletedChange={handleLogoWasDeletedChange}
          originalLogoUrl={logoWasDeleted ? null : (originalValuesRef.current?.originalLogoUrl ?? null)}
        />

        <SocialMedia
          formState={formState}
          formErrors={formErrors}
          setFormState={setFormState}
          setFormErrors={setFormErrors}
        />

        <div className={styles["business-profile__actions"]}>
          <div className={styles["business-profile__actions__divider"]}></div>
          <div className={styles["business-profile__actions__buttons"]}>
            <Button
              variant="outlined"
              color="secondary"
              text={t('innerLayout.businessProfilePage.actions.cancel')}
              onClickHandler={handleCancel}
              disabled={isCancelDisabled}
            />
            <Button
              variant="contained"
              text={t('innerLayout.businessProfilePage.actions.saveChanges')}
              onClickHandler={handleSave}
              disabled={isSaveDisabled}
              loading={updateMutation.isPending}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
